import { SectionCard, ChoiceCard, Hint } from '../UIComponents';
import {
  parseCarbonPulseExcelFile,
  buildCarbonPulseFormPatch,
} from '../../../domain/data-collection/carbonPulseExcel';
import { getInitialFormData } from '../../../domain/data-collection/defaults';

const INTERACTION = '#026F6D';
const MUTED = '#64748b';

export default function DataSourcesStep({
  formData,
  setField,
  setFormData,
  isReadOnly,
}) {
  const gcs = formData.gcsRequested === true ? true : false;
  const silverdat = formData.silverdatRequested === true ? true : false;

  const carbonPulseImported = formData.carbonPulseImportSuccessful === true;
  const availableYears = Array.isArray(formData.carbonPulseAvailableYears)
    ? formData.carbonPulseAvailableYears
    : [];

  function applyCarbonPulseYear(year) {
    const { patch, importedFields } = buildCarbonPulseFormPatch(
      formData.carbonPulseJahreswerte,
      year,
      formData.carbonPulseGebaeudeM2
    );

    const defaults = getInitialFormData();
    const previouslyImportedFields = Array.isArray(
      formData.carbonPulseImportedFields
    )
      ? formData.carbonPulseImportedFields
      : [];

    const resetFields = {};
    previouslyImportedFields.forEach(field => {
      if (!importedFields.includes(field)) {
        resetFields[field] = defaults[field];
      }
    });

    setFormData(previous => ({
      ...previous,
      ...resetFields,
      ...patch,
      carbonPulseSelectedYear: year,
      carbonPulseImportedFields: importedFields,
      carbonPulseImportMessage:
        importedFields.length > 0
          ? `Daten für ${year} übernommen. Bitte übernommene Werte prüfen.`
          : `Für ${year} wurden keine verwertbaren Werte gefunden.`,
    }));
  }

  async function handleCarbonPulseFile(file) {
    if (!file) return;

    setField('carbonPulseImportError', '');
    setField('carbonPulseImportMessage', '');

    try {
      const { availableYears: years, jahreswerte, gebaeudeM2 } =
        await parseCarbonPulseExcelFile(file);

      const referenceYear = years.includes(formData.reportingYear)
        ? formData.reportingYear
        : years.at(-1);

      const { patch, importedFields } = buildCarbonPulseFormPatch(
        jahreswerte,
        referenceYear,
        gebaeudeM2
      );

      setFormData(previous => ({
        ...previous,
        ...patch,
        carbonPulseFileName: file.name,
        carbonPulseImportSuccessful: true,
        carbonPulseAvailableYears: years,
        carbonPulseSelectedYear: referenceYear,
        carbonPulseJahreswerte: jahreswerte,
        carbonPulseGebaeudeM2: gebaeudeM2,
        carbonPulseImportedFields: importedFields,
        carbonPulseImportMessage: `Daten für ${referenceYear} übernommen. Bitte übernommene Werte prüfen.`,
        carbonPulseImportError: '',
      }));
    } catch (error) {
      setFormData(previous => ({
        ...previous,
        carbonPulseFileName: '',
        carbonPulseImportSuccessful: false,
        carbonPulseAvailableYears: [],
        carbonPulseSelectedYear: '',
        carbonPulseJahreswerte: null,
        carbonPulseGebaeudeM2: null,
        carbonPulseImportedFields: [],
        carbonPulseImportMessage: '',
        carbonPulseImportError:
          error instanceof Error
            ? error.message
            : 'Die Datei konnte nicht eingelesen werden.',
      }));
    }
  }

  function removeCarbonPulseImport() {
    const defaults = getInitialFormData();
    const importedFields = Array.isArray(formData.carbonPulseImportedFields)
      ? formData.carbonPulseImportedFields
      : [];

    setFormData(previous => {
      const resetFields = {};
      importedFields.forEach(field => {
        resetFields[field] = defaults[field];
      });

      return {
        ...previous,
        ...resetFields,
        carbonPulseFileName: '',
        carbonPulseImportSuccessful: false,
        carbonPulseAvailableYears: [],
        carbonPulseSelectedYear: '',
        carbonPulseJahreswerte: null,
        carbonPulseGebaeudeM2: null,
        carbonPulseImportedFields: [],
        carbonPulseImportMessage: '',
        carbonPulseImportError: '',
      };
    });
  }

  return (
    <div>
      <SectionCard title="Carbonpulse Excel-Import" badge="optional">
        <p style={{ fontSize: 14, color: MUTED, marginTop: 0, marginBottom: 16 }}>
          Sie können die Vorab-Checkliste von Carbonpulse hochladen. Erkannte Werte
          (Betriebsfläche, Strom, PV, Heizung, Lack, Reparaturen) werden für das
          gewählte Jahr automatisch übernommen und in den entsprechenden Schritten
          zur Prüfung und Korrektur angezeigt.
        </p>

        <input
          type="file"
          accept=".xlsx,.xls"
          onChange={async event => {
            const file = event.target.files?.[0];

            if (file) {
              await handleCarbonPulseFile(file);
            }

            event.target.value = '';
          }}
          disabled={isReadOnly || carbonPulseImported}
          id="carbonpulse-upload"
          style={{ display: 'none' }}
        />

        {!carbonPulseImported && (
          <label
            htmlFor="carbonpulse-upload"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '9px 14px',
              border: `1px dashed ${INTERACTION}`,
              borderRadius: 8,
              cursor: isReadOnly ? 'default' : 'pointer',
              color: INTERACTION,
              background: '#ffffff',
              fontSize: 14,
            }}
          >
            📁 Carbonpulse-Excel auswählen (xlsx / xls)…
          </label>
        )}

        {carbonPulseImported && formData.carbonPulseFileName && (
          <p style={{ margin: '0 0 8px', fontSize: 13, color: MUTED }}>
            Importierte Datei: <strong>{formData.carbonPulseFileName}</strong>
          </p>
        )}

        {carbonPulseImported && availableYears.length > 1 && (
          <div style={{ marginTop: 12 }}>
            <label className="label" style={{ display: 'block', marginBottom: 8 }}>
              Jahr für Übernahme wählen
            </label>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {availableYears.map(year => (
                <ChoiceCard
                  key={year}
                  label={String(year)}
                  selected={formData.carbonPulseSelectedYear === year}
                  onClick={() => applyCarbonPulseYear(year)}
                  disabled={isReadOnly}
                />
              ))}
            </div>
          </div>
        )}

        {formData.carbonPulseImportMessage && (
          <Hint type="success">{formData.carbonPulseImportMessage}</Hint>
        )}

        {formData.carbonPulseImportError && (
          <Hint type="warning">{formData.carbonPulseImportError}</Hint>
        )}

        {carbonPulseImported && (
          <div style={{ marginTop: 12 }}>
            <button
              type="button"
              onClick={removeCarbonPulseImport}
              disabled={isReadOnly}
              style={{
                background: '#fff1f2',
                color: '#b91c1c',
                border: '1px solid #fecaca',
                borderRadius: 8,
                padding: '9px 14px',
                fontSize: 14,
                fontWeight: 600,
                cursor: isReadOnly ? 'default' : 'pointer',
                fontFamily: 'inherit',
                opacity: isReadOnly ? 0.6 : 1,
              }}
            >
              Importierte Datei entfernen
            </button>
          </div>
        )}

        <Hint>
          Erwartet wird eine Vorab-Checkliste im Carbonpulse-Format (Tabellenblatt
          „Check-Liste“). Die übernommenen Werte können danach in den jeweiligen
          Schritten manuell überschrieben werden.
        </Hint>
      </SectionCard>

      <SectionCard title="GCS AG Anbindung" badge="optional">
        <p style={{ fontSize: 14, color: MUTED, marginTop: 0, marginBottom: 16 }}>
          Möchten Sie GCS (Garagen-Computerservice AG) für automatische Datenbefüllung anbinden?
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <ChoiceCard
            label="Ja, GCS anbinden"
            description="Noch nicht verfügbar"
            selected={gcs === true}
            onClick={() => {}}
            disabled
          />
          <ChoiceCard
            label="Nein, manuell weiter"
            description="Alle Felder manuell erfassen"
            selected={gcs === false}
            onClick={() => setField('gcsRequested', false)}
            disabled={isReadOnly}
          />
        </div>
        <Hint>
          An der GCS-Anbindung arbeiten wir noch. Bis dahin erfolgt die Datenerhebung manuell.
        </Hint>
      </SectionCard>

      <SectionCard title="auto-i-dat / SilverDAT Anbindung" badge="optional">
        <p style={{ fontSize: 14, color: MUTED, marginTop: 0, marginBottom: 16 }}>
          Möchten Sie auto-i-dat / SilverDAT für automatische Reparatur- und Schadensdaten anbinden?
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <ChoiceCard
            label="Ja, auto-i-dat anbinden"
            description="Noch nicht verfügbar"
            selected={silverdat === true}
            onClick={() => {}}
            disabled
          />
          <ChoiceCard
            label="Nein, manuell weiter"
            description="Reparatur- und Schadensdaten manuell erfassen"
            selected={silverdat === false}
            onClick={() => setField('silverdatRequested', false)}
            disabled={isReadOnly}
          />
        </div>
        <Hint>
          An der auto-i-dat / SilverDAT-Anbindung arbeiten wir noch. Bis dahin erfolgt die Datenerhebung manuell.
        </Hint>
      </SectionCard>
    </div>
  );
}